"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenant-context";
import { CANDIDATE_PUBLIC_ROUTES } from "@/lib/candidate-public-routes";

// Powered by VHire badge component
function PoweredByVHire({ style, primaryColor, tenantSlug }: { style: 'text' | 'badge' | 'logo'; primaryColor?: string; tenantSlug?: string }) {
  const refUrl = `https://vhire.com?ref=${tenantSlug || 'tenant'}`;
  const color = primaryColor || '#6366f1';

  if (style === 'badge') {
    return (
      <a
        href={refUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-muted rounded-full text-xs text-muted-foreground hover:bg-muted/80 transition"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill={color}>
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
        <span>Powered by <span style={{ color }} className="font-semibold">VHire</span></span>
      </a>
    );
  } else if (style === 'logo') {
    return (
      <a
        href={refUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-3 opacity-60 hover:opacity-100 transition"
      >
        {/* This tenant-branding footer is embedded outside Next image optimization. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="https://vhire.com/logo-white.svg" alt="Powered by VHire" className="h-5" />
      </a>
    );
  } else {
    // Default: text style
    return (
      <p className="mt-2 text-sm text-muted-foreground">
        Powered by{" "}
        <a
          href={refUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color }}
          className="font-medium hover:underline"
        >
          VHire
        </a>
      </p>
    );
  }
}

// Social links component
function SocialLinks({ links }: { links: { facebook?: string; twitter?: string; linkedin?: string; instagram?: string } }) {
  return (
    <div className="flex justify-center space-x-4 mb-4">
      {links.linkedin && (
        <a href={links.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground hover:text-foreground transition">
          <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        </a>
      )}
      {links.twitter && (
        <a href={links.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter / X" className="text-muted-foreground hover:text-foreground transition">
          <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </a>
      )}
      {links.facebook && (
        <a href={links.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-foreground transition">
          <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </a>
      )}
      {links.instagram && (
        <a href={links.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-foreground transition">
          <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
        </a>
      )}
    </div>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { tenant, isTenantDomain } = useTenant();
  const [mounted, setMounted] = useState(false);

  // TenantProvider hydrates its edge cookie after the app shell starts
  // hydrating. A streamed Footer boundary can therefore observe tenant state
  // on the client while its server HTML is still the generic VHire footer.
  // Gate tenant-dependent markup behind this component's own mount so the
  // server and first client render are byte-for-byte the same; branding swaps
  // in only after hydration has committed.
  useEffect(() => setMounted(true), []);

  // If on a tenant domain, render tenant-branded footer
  if (mounted && isTenantDomain && tenant) {
    const footer = tenant.branding?.footer;
    const companyName = footer?.company_name || tenant.company_name || tenant.name;
    const showSocialLinks = footer?.show_social_links !== false;
    const socialLinks = tenant.branding?.social_links;
    const hasSocialLinks = socialLinks && (socialLinks.linkedin || socialLinks.twitter || socialLinks.facebook || socialLinks.instagram);

    return (
      <footer className="w-full border-t bg-muted/30 py-8 text-sm">
        <div className="container mx-auto px-4 md:px-6 text-center">
          {/* Social Links */}
          {showSocialLinks && hasSocialLinks && (
            <SocialLinks links={socialLinks} />
          )}

          {/* Custom Footer Links */}
          {footer?.links && footer.links.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              {footer.links.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  className="text-muted-foreground hover:text-foreground transition text-sm"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* Company Website Link */}
          {tenant.company_web_url && (
            <a
              href={tenant.company_web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
              </svg>
              <span>Visit {companyName} Website</span>
            </a>
          )}

          {/* Footer Tagline */}
          {footer?.tagline && (
            <p className="text-sm text-muted-foreground mb-2">{footer.tagline}</p>
          )}

          {/* Copyright */}
          <p className="text-sm text-muted-foreground">
            &copy; {currentYear} {companyName}. All rights reserved.
          </p>

          {/* Powered by VHire */}
          {(footer?.powered_by_vhire !== false) && (
            <PoweredByVHire
              style={footer?.powered_by_style || 'badge'}
              primaryColor={tenant.branding?.primary_color}
              tenantSlug={tenant.slug}
            />
          )}
        </div>
      </footer>
    );
  }

  // Default VHire footer for main domain
  return (
    <footer className="w-full border-t bg-background py-12 text-sm">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-10">
          {/* Column 1: Company Identity */}
          <div className="space-y-4">
            <h3 className="font-semibold tracking-tight text-foreground">
              Talgrid Tech Private Limited
            </h3>
            <div className="text-muted-foreground space-y-1">
              <p>Bengaluru, India</p>
              <a
                href="mailto:support@vhire.com"
                className="hover:text-foreground transition-colors"
              >
                support@vhire.com
              </a>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Candidate hiring technology operated by Talgrid Tech Private Limited.
            </p>
          </div>

          {/* Column 2: Candidate destinations */}
          <div className="space-y-4">
            <h3 className="font-semibold tracking-tight text-foreground">For candidates</h3>
            <nav className="flex flex-col gap-2 text-muted-foreground">
              <Link href="/" prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Jobs
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.companies} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Companies
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.careerGuide} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Career Guide
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.trust} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Trust
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.safety} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Candidate Safety
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.help} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Help
              </Link>
            </nav>
          </div>

          {/* Column 3: VHire identity */}
          <div className="space-y-4">
            <h3 className="font-semibold tracking-tight text-foreground">VHire</h3>
            <nav className="flex flex-col gap-2 text-muted-foreground">
              <Link href={CANDIDATE_PUBLIC_ROUTES.about} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                About
              </Link>
              <Link href="/employer" prefetch={false} className="hover:text-foreground transition-colors w-fit">
                For Employers
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.contact} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Contact
              </Link>
              <Link href={CANDIDATE_PUBLIC_ROUTES.editorialPolicy} prefetch={false} className="hover:text-foreground transition-colors w-fit">
                Editorial Policy
              </Link>
            </nav>
          </div>

          {/* Column 4: Legal & Compliance */}
          <div className="space-y-4 lg:text-right">
            <h3 className="font-semibold tracking-tight text-foreground">Legal</h3>
            <nav className="flex flex-col gap-2 lg:items-end text-muted-foreground">
              {/*
                prefetch={false} — these legal pages are infrequently visited
                and the Footer renders on every route (including /admin/* via
                FooterWrapper). Without this, every page load fires three RSC
                prefetches for /legal/* that the user almost never follows.
                See perf benchmark 2026-05-21 (Phase 1.4).
              */}
              <Link
                href="/legal/privacy"
                prefetch={false}
                className="hover:text-foreground transition-colors w-fit"
              >
                Privacy Policy
              </Link>
              <Link
                href="/legal/terms"
                prefetch={false}
                className="hover:text-foreground transition-colors w-fit"
              >
                Terms of Service
              </Link>
              <Link
                href="/legal/cookies"
                prefetch={false}
                className="hover:text-foreground transition-colors w-fit"
              >
                Cookie Policy
              </Link>
            </nav>
            <p className="text-xs text-muted-foreground">
              &copy; {currentYear} Talgrid Tech Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
